import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { axios } from '../hooks/useAuth';

const EMOJIS = ['🎤','🎵','🎶','🎸','🎹','🎺','🎻','🥁','🎙️','🎼','🎧','🌟','⭐','🦋','🌈','🔥','💫','🎭'];

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const [compilations, setCompilations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ displayName: user?.displayName || '', emoji: user?.emoji || '🎤' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get('/api/compilations/my')
      .then(r => setCompilations(r.data.compilations || []))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      setMsg('✅ Profile updated!');
      setEditing(false);
    } catch {
      setMsg('❌ Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page page-enter" style={{ maxWidth: 900 }}>
      {/* Profile Header */}
      <div className="card" style={{ padding: 32, marginBottom: 28 }}>
        <div className="flex items-center gap-4">
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 40, flexShrink: 0,
            boxShadow: '0 0 30px rgba(192,38,211,0.4)',
          }}>
            {user?.emoji}
          </div>
          <div className="flex-1">
            <h1 style={{ fontSize: 28, fontWeight: 900 }}>{user?.displayName}</h1>
            <p className="text-secondary">@{user?.username}</p>
            <p className="text-muted text-sm">{user?.email}</p>
          </div>
          <div>
            <button className="btn btn-secondary" onClick={() => setEditing(!editing)}>
              {editing ? '✕ Cancel' : '✏️ Edit Profile'}
            </button>
          </div>
        </div>

        {editing && (
          <form onSubmit={handleSave} style={{ marginTop: 24, borderTop: '1px solid var(--border-color)', paddingTop: 24 }}>
            <div className="form-group">
              <label className="form-label">Display Name</label>
              <input className="form-input" value={form.displayName}
                onChange={e => setForm({ ...form, displayName: e.target.value })}
                style={{ maxWidth: 300 }} />
            </div>
            <div className="form-group">
              <label className="form-label">Your Emoji</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {EMOJIS.map(em => (
                  <button key={em} type="button"
                    onClick={() => setForm({ ...form, emoji: em })}
                    style={{
                      width: 44, height: 44, borderRadius: 8, fontSize: 24,
                      border: `2px solid ${form.emoji === em ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                      background: form.emoji === em ? 'rgba(192,38,211,0.15)' : 'var(--bg-elevated)',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}>
                    {em}
                  </button>
                ))}
              </div>
            </div>
            {msg && <div className={`alert ${msg.startsWith('✅') ? 'alert-success' : 'alert-error'}`}>{msg}</div>}
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '⏳ Saving...' : '💾 Save Changes'}
            </button>
          </form>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid-3" style={{ marginBottom: 28 }}>
        {[
          { icon: '🎙️', label: 'Compilations', value: compilations.length },
          { icon: '🎵', label: 'Songs Sung', value: compilations.reduce((acc, c) => acc + c.trackCount, 0) },
          { icon: '👁️', label: 'Total Views', value: compilations.reduce((acc, c) => acc + c.viewCount, 0) },
        ].map((s, i) => (
          <div key={i} className="card" style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
            <div style={{
              fontSize: 32, fontWeight: 900, fontFamily: 'var(--font-mono)',
              background: 'linear-gradient(135deg, var(--accent-glow), var(--accent-primary))',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>{s.value}</div>
            <div className="text-muted text-sm" style={{ marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* My Compilations */}
      <h2 style={{ marginBottom: 16 }}>🎙️ My Compilations</h2>

      {loading ? (
        <p className="text-muted">Loading...</p>
      ) : compilations.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎙️</div>
          <h3 className="text-secondary">No recordings yet</h3>
          <p className="text-muted">Pick a song and record your first track!</p>
          <Link to="/songs" className="btn btn-primary" style={{ marginTop: 16 }}>Browse Songs</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {compilations.map(c => (
            <Link key={c.id} to={`/compilation/${c.id}`}
              style={{ textDecoration: 'none' }}
              className="card card-hover">
              <div className="flex items-center gap-16">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>{c.title}</div>
                  <div className="text-secondary text-sm">
                    {c.song?.title} — {c.song?.artist}
                  </div>
                </div>
                <div className="flex gap-4 text-sm text-muted">
                  <span>🎙️ {c.trackCount}/{c.maxTracks} tracks</span>
                  <span>👁 {c.viewCount} views</span>
                  <span className={`badge ${c.status === 'active' ? 'badge-green' : 'badge-fuchsia'}`}>
                    {c.status}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
